import React, { useEffect, useState } from 'react';
import { Manga, TIER_ORDER } from '../types/manga';
import { getAllDownloadedChapterNums } from '../services/mangaOfflineDB';
import { ReadingProgress, getAllReadingProgress, pickResumeChapter } from '../services/readingProgress';
import { formatImageUrl, DEFAULT_COVER_PLACEHOLDER } from '../utils/helpers';
import { TierHeading } from './TierBoard';
import { MangaReaderModal } from './MangaReaderModal';
import { OfflineChapterListModal } from './OfflineChapterListModal';
import { X, Loader2, BookOpen, Play, Trash2 } from 'lucide-react';

interface DownloadLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  mangaList: Manga[];
  onUpdateManga: (updated: Manga) => void;
}

interface LibraryEntry {
  manga: Manga;
  chapters: number[];
  progress: ReadingProgress | null;
}

export const DownloadLibraryModal: React.FC<DownloadLibraryModalProps> = ({
  isOpen,
  onClose,
  mangaList,
  onUpdateManga,
}) => {
  const [downloads, setDownloads] = useState<Map<string, number[]> | null>(null);
  const [progressMap, setProgressMap] = useState<Record<string, ReadingProgress>>({});
  const [reader, setReader] = useState<{
    manga: Manga;
    chapterNum: number;
    chapters: number[];
  } | null>(null);
  // Manga whose chapter list (delete / pick a chapter) is open
  const [managingId, setManagingId] = useState<string | null>(null);

  const refresh = () => {
    setProgressMap(getAllReadingProgress());
    getAllDownloadedChapterNums()
      .then(setDownloads)
      .catch(() => setDownloads(new Map()));
  };

  useEffect(() => {
    if (isOpen) {
      setDownloads(null);
      refresh();
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // The reader unlocks body scroll when it closes; lock it again while the library stays open
  useEffect(() => {
    if (isOpen && !reader) document.body.style.overflow = 'hidden';
  }, [isOpen, reader]);

  if (!isOpen) return null;

  const entries: LibraryEntry[] = mangaList
    .filter((m) => (downloads?.get(m.id)?.length || 0) > 0)
    .map((m) => ({
      manga: m,
      chapters: downloads!.get(m.id)!,
      progress: progressMap[m.id] || null,
    }));

  const openReader = (entry: LibraryEntry) => {
    const pick = pickResumeChapter(entry.chapters, entry.progress, Number(entry.manga.readCh) || 0);
    if (pick)
      setReader({
        manga: entry.manga,
        chapterNum: pick.chapterNum,
        chapters: entry.chapters,
      });
  };

  // Keep the reader's manga in sync with edits (e.g. readCh bumped by "mark read")
  const readerManga = reader ? mangaList.find((m) => m.id === reader.manga.id) || reader.manga : null;
  const managingManga = managingId ? mangaList.find((m) => m.id === managingId) || null : null;

  return (
    <>
      <div className="modal-overlay safe-area-modal">
        <div onClick={(e) => e.stopPropagation()} className="modal-panel max-w-5xl">
          <div className="modal-header">
            <div className="flex items-center gap-2.5 min-w-0">
              <BookOpen className="w-4 h-4 text-violet-400 shrink-0" />
              <h3 className="modal-title">คลังดาวน์โหลด</h3>
              {downloads && (
                <span className="text-xs text-zinc-500 tabular-nums">{entries.length} เรื่อง</span>
              )}
            </div>
            <button onClick={onClose} title="ปิด" className="icon-btn -mr-2">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="modal-body space-y-8" style={{ WebkitOverflowScrolling: 'touch' }}>
            {!downloads ? (
              <div className="flex items-center justify-center py-16 text-zinc-500">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : entries.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-sm sm:text-base text-zinc-300">ยังไม่มีเรื่องที่ดาวน์โหลดไว้</p>
                <p className="text-xs sm:text-sm text-zinc-500 mt-1.5">
                  แตะการ์ดมังงะ แล้วกด "ดาวน์โหลดตอน" เพื่อเก็บไว้อ่านออฟไลน์
                </p>
              </div>
            ) : (
              TIER_ORDER.map((grade) => {
                const tierEntries = entries
                  .filter((e) => e.manga.grade === grade)
                  // Most recently read first, then board order
                  .sort(
                    (a, b) =>
                      (b.progress?.updatedAt || 0) - (a.progress?.updatedAt || 0) ||
                      (a.manga.order ?? 0) - (b.manga.order ?? 0),
                  );
                if (tierEntries.length === 0) return null;

                return (
                  <section key={grade}>
                    <TierHeading grade={grade} count={tierEntries.length} />
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-x-2.5 gap-y-5 sm:gap-x-4 sm:gap-y-6 items-stretch">
                      {tierEntries.map((entry) => (
                        <LibraryCard
                          key={entry.manga.id}
                          entry={entry}
                          onOpen={() => openReader(entry)}
                          onManage={() => setManagingId(entry.manga.id)}
                        />
                      ))}
                    </div>
                  </section>
                );
              })
            )}
          </div>
        </div>
      </div>

      <OfflineChapterListModal
        isOpen={Boolean(managingManga)}
        onClose={() => setManagingId(null)}
        manga={managingManga}
        onOpenReader={(chapterNum, list) => {
          if (managingManga) setReader({ manga: managingManga, chapterNum, chapters: list });
        }}
        onChaptersChanged={refresh}
      />

      {reader && readerManga && (
        <MangaReaderModal
          isOpen
          onClose={() => {
            setReader(null);
            refresh();
          }}
          mangaId={readerManga.id}
          mangaTitle={readerManga.title}
          initialChapterNum={reader.chapterNum}
          availableChapterNums={reader.chapters}
          onMarkRead={(chapterNum) => {
            if (chapterNum > (readerManga.readCh || 0)) onUpdateManga({ ...readerManga, readCh: chapterNum });
          }}
        />
      )}
    </>
  );
};

const LibraryCard: React.FC<{
  entry: LibraryEntry;
  onOpen: () => void;
  onManage: () => void;
}> = ({ entry, onOpen, onManage }) => {
  const [imgError, setImgError] = useState(false);
  const { manga, chapters, progress } = entry;
  const pick = pickResumeChapter(chapters, progress, Number(manga.readCh) || 0);
  const lastDownloaded = chapters[chapters.length - 1];
  const allDone = Boolean(progress?.finished && progress.chapterNum >= lastDownloaded);

  // Fill of the bar under the cover: how far into the chapter we'll resume
  const resumeFraction =
    pick?.resume && progress && progress.totalPages > 0
      ? Math.min(1, (progress.pageIndex + progress.pageOffset) / progress.totalPages)
      : 0;

  let status: React.ReactNode;
  if (allDone) {
    status = <span className="text-emerald-400">อ่านครบที่โหลดไว้</span>;
  } else if (pick?.resume && progress) {
    status = (
      <span className="text-violet-300">
        อ่านต่อ ตอน {pick.chapterNum} · หน้า {progress.pageIndex + 1}/{progress.totalPages}
      </span>
    );
  } else if (pick) {
    status = <span className="text-zinc-300">เริ่มตอน {pick.chapterNum}</span>;
  }

  return (
    <div className="group relative">
      <button type="button" onClick={onOpen} className="flex flex-col w-full text-left focus:outline-none">
        <div className="relative aspect-[3/4] w-full rounded-lg sm:rounded-xl bg-zinc-900 overflow-hidden ring-1 ring-inset ring-white/[0.06]">
          <img
            src={imgError ? DEFAULT_COVER_PLACEHOLDER : formatImageUrl(manga.imageUrl)}
            alt={manga.title}
            onError={() => setImgError(true)}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover object-center group-hover:scale-[1.03] transition-transform duration-300"
          />
          <span className="absolute top-1.5 left-1.5 px-1.5 py-[3px] rounded-md bg-black/70 backdrop-blur text-zinc-100 text-[9px] sm:text-[10px] font-medium leading-none tabular-nums">
            โหลดไว้ {chapters.length} ตอน
          </span>
          <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="w-10 h-10 rounded-full bg-zinc-50 text-zinc-900 flex items-center justify-center shadow-lg">
              <Play className="w-4 h-4 ml-0.5" />
            </span>
          </span>
          {resumeFraction > 0 && (
            <span className="absolute bottom-0 left-0 right-0 h-1 bg-black/50">
              <span className="block h-full bg-violet-400" style={{ width: `${resumeFraction * 100}%` }} />
            </span>
          )}
        </div>
        <h4 className="mt-2 text-[12px] sm:text-sm font-medium text-zinc-100 leading-snug line-clamp-2">
          {manga.title}
        </h4>
        <p className="mt-1 text-[10.5px] sm:text-xs tabular-nums">{status}</p>
        <p className="mt-0.5 text-[10px] sm:text-[11px] text-zinc-500 tabular-nums">
          ตอน {chapters[0]}
          {chapters.length > 1 ? `–${lastDownloaded}` : ''}
        </p>
      </button>
      {/* Sibling of the open button (buttons can't nest): manage / delete downloaded chapters */}
      <button
        type="button"
        onClick={onManage}
        title="จัดการ / ลบตอนที่โหลดไว้"
        className="absolute top-1.5 right-1.5 w-7 h-7 rounded-md bg-black/70 backdrop-blur text-zinc-300 hover:text-rose-300 hover:bg-black/85 flex items-center justify-center ring-1 ring-white/10 transition-colors"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};
